"use client"

import { useState, useEffect } from "react"
import type { Zone } from "@/lib/zones"
import { Card, CardContent } from "@/components/ui/card"
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react"
import { motion } from "framer-motion"
import { getTodayInPrague } from "@/lib/timezone"

interface ReflectionMessageProps {
  zone: Zone
  userId: string
}

interface CachedReflection {
  zoneId: string
  message: string
  timestamp: number
  date: string // Date when this reflection was generated (YYYY-MM-DD)
  userId?: string
}

const CACHE_KEY = "reflection-cache"

export function ReflectionMessage({ zone, userId }: ReflectionMessageProps) {
  const [message, setMessage] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [disabled, setDisabled] = useState(false)

  // Get today's date in YYYY-MM-DD format (Czech timezone)
  const getTodayDate = (): string => {
    return getTodayInPrague()
  }

  // Get cached reflection if available for today's zone and user
  const getCachedReflection = (zoneId: string): string | null => {
    if (typeof window === "undefined") return null

    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const data: CachedReflection = JSON.parse(cached)
      const today = getTodayDate()

      // Cache is valid only if it's for the same zone, date, AND user
      // This prevents showing cached reflections from other users
      if (data.zoneId === zoneId && data.date === today && data.userId === userId) {
        return data.message
      }
    } catch (err) {
      console.error("Error reading reflection cache:", err)
    }

    return null
  }

  // Save reflection to cache with today's date and userId
  const setCachedReflection = (zoneId: string, message: string) => {
    if (typeof window === "undefined") return

    try {
      const data: CachedReflection = {
        zoneId,
        message,
        timestamp: Date.now(),
        date: getTodayDate(),
        userId,
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(data))
    } catch (err) {
      console.error("Error saving reflection cache:", err)
    }
  }

  // Static fallback messages
  const getStaticMessage = (zone: Zone): string => {
    const zoneNum = zone.number

    if (zoneNum === "R") {
      return "Nacházíš se v regenerační zóně – to je čas, kdy se tělo léčí. Dopřej si klid a žádné hodnocení."
    }

    if (typeof zoneNum === "number") {
      if (zoneNum <= 3) {
        return `Dnes jsi v zóně ${zoneNum}. Je v pořádku, že to vidíš. Zkus malý krok – procházka, světlo, hudba.`
      } else if (zoneNum <= 5) {
        return `Dnes jsi v zóně ${zoneNum}. Krásně, že vnímáš svůj rytmus. Užívej si tento čas.`
      } else if (zoneNum <= 7) {
        return `Dnes jsi v zóně ${zoneNum} – výborně, že to vidíš. Zkus dneska dýchat pomaleji a dopřej si pauzy.`
      } else {
        return `Dnes jsi v zóně ${zoneNum}. Je důležité, že to vnímáš. Odpočinek teď není slabost, ale nutnost.`
      }
    }

    return "Stav uložen. Vědomost o sobě je první krok k rovnováze."
  }

  useEffect(() => {
    let aborted = false

    const fetchAIReflection = async () => {
      try {
        setLoading(true)
        setError(false)

        // Check cache first
        const cachedMessage = getCachedReflection(zone.id)
        if (cachedMessage) {
          setMessage(cachedMessage)
          setLoading(false)
          return
        }

        // Don't show static message while loading - wait for AI response
        // Loading state will show spinner instead

        const response = await fetch("/api/ai/reflection", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            zoneId: zone.id,
            includeHistory: true,
          }),
        })

        if (!response.ok) {
          // Try to parse error response
          let errorMessage = "AI reflection failed"
          let isDisabled = false

          try {
            const contentType = response.headers.get("content-type")

            if (contentType?.includes("application/json")) {
              const errorData = await response.json()
              errorMessage = errorData.error || errorMessage
              isDisabled = errorData.disabled || false
            } else {
              const textError = await response.text()
              if (textError) errorMessage = textError
            }
          } catch (parseError) {
            console.error("Failed to parse error response:", parseError)
          }

          // Handle specific error cases
          if (response.status === 403 && isDisabled) {
            // AI analysis is disabled - show disabled state
            if (!aborted) {
              setDisabled(true)
              setLoading(false)
            }
            return
          }

          // Log specific error details for debugging
          console.error("AI reflection API error:")
          console.error("  Status:", response.status)
          console.error("  Status Text:", response.statusText)
          console.error("  Message:", errorMessage)

          // Handle other error cases
          if (response.status === 401) {
            console.error("  Issue: Authentication required - user not signed in")
          } else if (response.status === 429) {
            console.error("  Issue: Rate limit exceeded")
          }

          throw new Error(errorMessage)
        }

        // Handle streaming response
        const reader = response.body?.getReader()
        const decoder = new TextDecoder()

        if (!reader) {
          throw new Error("No response body")
        }

        let accumulated = ""

        while (true) {
          const { done, value } = await reader.read()

          if (done || aborted) {
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          accumulated += chunk

          if (!aborted) {
            setMessage(accumulated)
          }
        }

        // Save to cache after successful fetch
        if (accumulated && !aborted) {
          setCachedReflection(zone.id, accumulated)
        }

        setLoading(false)
      } catch (err) {
        console.error("Error fetching AI reflection:", err)

        if (!aborted) {
          setError(true)
          setMessage(getStaticMessage(zone))
          setLoading(false)
        }
      }
    }

    fetchAIReflection()

    // Cleanup function to abort fetch on unmount
    return () => {
      aborted = true
    }
  }, [zone.id, zone.number, userId])

  // If AI is disabled, show disabled message
  if (disabled) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
        <Card className="bg-muted border-muted">
          <CardContent className="pt-3 sm:pt-4 pb-3 sm:pb-4 px-3 sm:px-6">
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              AI reflexe jsou vypnuté. Můžete je zapnout v nastavení aplikace.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  const displayMessage = message || (error ? getStaticMessage(zone) : "")

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6 px-3 sm:px-6">
          <div className="flex items-start gap-2.5 sm:gap-3">
            {loading && !message ? (
              <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0 animate-spin" />
            ) : error ? (
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              {displayMessage ? (
                <p className="text-xs sm:text-sm leading-relaxed text-pretty">{displayMessage}</p>
              ) : (
                <div className="space-y-2">
                  <div className="h-3 bg-primary/10 rounded animate-pulse w-full" />
                  <div className="h-3 bg-primary/10 rounded animate-pulse w-4/5" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
